// frontend/src/components/admin/LicenceManagementModal.jsx
// ============================================================================
// MODAL GESTION DE LICENCE — THEME-AWARE (Light/Dark)
// v2 : stockage supprimé, limites depuis tarifs, prix + frais affichés par plan
// ============================================================================

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Shield, Users, TrendingUp,
  CheckCircle, Database, Sliders, Info, Zap, Tag, 
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { Toast } from '../ui/Toast';
import { useTheme } from '../../contexts/ThemeContext';

// ============================================================================
// SOUS-COMPOSANTS
// ============================================================================

const Label = ({ children, t }) => (
  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] mb-1.5"
    style={{ color: t.textSecondary }}>
    {children}
  </label>
);

const NumberInput = ({ value, onChange, min, step, t }) => (
  <input type="number" min={min} step={step} value={value} onChange={onChange}
    className="w-full px-3.5 py-2.5 rounded-lg text-sm font-medium border outline-none transition-all duration-200"
    style={{ backgroundColor: t.bgInput, borderColor: t.border, color: t.text }}
    onFocus={e => { e.target.style.backgroundColor = t.bgInputFocus; e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
    onBlur={e  => { e.target.style.backgroundColor = t.bgInput; e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }} />
);

const TabBtn = ({ active, icon: Icon, label, onClick, t }) => (
  <button onClick={onClick}
    className="flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-semibold border-b-2 transition-all duration-200 -mb-px"
    style={{ borderColor: active ? t.primary : 'transparent', color: active ? t.primaryText : t.textMuted }}
    onMouseEnter={e => { if (!active) e.currentTarget.style.color = t.text; }}
    onMouseLeave={e => { if (!active) e.currentTarget.style.color = t.textMuted; }}>
    <Icon size={14} />{label}
  </button>
);

const PlanStat = ({ icon: Icon, label, accent, t }) => (
  <div className="flex items-center gap-2.5">
    <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: accent + '12' }}>
      <Icon size={13} style={{ color: accent }} />
    </div>
    <span className="text-[12px] font-medium" style={{ color: t.textSecondary }}>{label}</span>
  </div>
);

// Barre résumé licence — employés + utilisateurs uniquement (stockage supprimé)
const LicenceSummary = ({ licence, t, accent }) => (
  <div className="flex items-center justify-between px-5 py-4 rounded-xl border"
    style={{ backgroundColor: t.bgCard, borderColor: t.border }}>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: accent + '12' }}>
        <Shield size={18} style={{ color: accent }} />
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: t.textMuted }}>Licence actuelle</div>
        <div className="text-[15px] font-bold capitalize" style={{ color: t.text }}>{licence.type_licence}</div>
      </div>
    </div>
    <div className="flex items-center gap-8">
      {[
        { value: licence.max_employes,     label: 'employés'     },
        { value: licence.max_utilisateurs, label: 'utilisateurs' },
      ].map(item => (
        <div key={item.label} className="text-right">
          <div className="text-[15px] font-bold tabular-nums" style={{ color: t.text }}>{item.value}</div>
          <div className="text-[10px] font-semibold uppercase" style={{ color: t.textMuted }}>{item.label}</div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const LicenceManagementModal = ({ company, onClose, onUpdate }) => {
  const t = useTheme();

  const [activeTab,       setActiveTab]       = useState('upgrade');
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [licence,         setLicence]         = useState(null);
  const [plans,           setPlans]           = useState({});
  const [toast,           setToast]           = useState({ show: false, message: '', type: '' });
  const [confirmOverride, setConfirmOverride] = useState(null);

  const [versionData, setVersionData] = useState({
    version_app: '',
    notes: '',
  });

  const [selectedType,    setSelectedType]    = useState('');
  // Limites de base + add-ons facturables
  const [customLimits, setCustomLimits] = useState({
    max_employes:       0,
    max_utilisateurs:   0,
    addon_employes:     0,  // employés supplémentaires (tranches de 10)
    addon_utilisateurs: 0,  // utilisateurs supplémentaires
  });



  const PLAN_ACCENTS = { basique: '#6366f1', standard: '#0ea5e9', premium: '#f59e0b' };

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  useEffect(() => { loadPlans(); loadLicence(); }, [company.id]);

  const loadPlans = async () => {
    try {
      const res = await superAdminApi.licences.getPlans();
      if (res.success) setPlans(res.plans);
    } catch { showToast('Impossible de charger les plans', 'error'); }
  };

 const loadLicence = async () => {
  try {
    setLoading(true);
    const res = await superAdminApi.licences.getLicence(company.id);
    if (res.success) {
      const lic = res.licence;
      setLicence(lic);
      setSelectedType(lic.type_licence);
      setCustomLimits({
        max_employes:       lic.max_employes       || 0,
        max_utilisateurs:   lic.max_utilisateurs   || 0,
        addon_employes:     lic.addon_employes     || 0,
        addon_utilisateurs: lic.addon_utilisateurs || 0,
      });
      setVersionData({                          // ← ici, à l'intérieur
        version_app: lic.version_app || '1.0.0',
        notes:       lic.notes       || '',
      });
    }
  } catch { showToast('Impossible de charger la licence', 'error'); }
  finally { setLoading(false); }
};



  const handleUpgrade = async (force = false) => {
    if (!selectedType) { showToast('Sélectionnez un plan', 'error'); return; }
    setSaving(true);
    try {
      const res = await superAdminApi.licences.upgrade(company.id, selectedType);
      if (!res.success) { showToast(res.message || 'Erreur', 'error'); return; }
      const flagRes = await superAdminApi.licences.applyToCompany(company.id, selectedType, force);
      if (!flagRes.success && flagRes.requires_confirmation) {
        setSaving(false);
        setConfirmOverride({ overrides: flagRes.overrides, message: flagRes.message });
        return;
      }
      showToast(`Licence ${plans[selectedType]?.label || selectedType} appliquée`);
      setTimeout(() => { onUpdate?.(); onClose(); }, 1400);
    } catch (err) { showToast(err.message || 'Erreur', 'error'); }
    finally { setSaving(false); }
  };

  const handleConfirmForce = async () => { setConfirmOverride(null); await handleUpgrade(true); };

  const handleSaveCustom = async () => {
    setSaving(true);
    try {
      const res = await superAdminApi.licences.updateLimits(company.id, customLimits);
      if (res.success) { showToast('Limites modifiées'); setTimeout(() => { onUpdate?.(); onClose(); }, 1200); }
    } catch (err) { showToast(err.message || 'Erreur', 'error'); }
    finally { setSaving(false); }
  };


    const handleSaveVersion = async () => {
    setSaving(true);
    try {
      const res = await superAdminApi.licences.updateLimits(company.id, {
        ...customLimits,
        version_app: versionData.version_app,
        notes:       versionData.notes,
      });
      if (res.success) {
        showToast(`Version ${versionData.version_app} enregistrée`);
        setTimeout(() => { onUpdate?.(); onClose(); }, 1200);
      }
    } catch (err) { showToast(err.message || 'Erreur', 'error'); }
    finally { setSaving(false); }
  };

  const currentAccent = PLAN_ACCENTS[licence?.type_licence] || t.primary;
  const modalBg = t.mode === 'dark' ? '#111620' : '#ffffff';
  const bodyBg  = t.mode === 'dark' ? t.bg : '#f6f7f9';

  if (loading) return ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: t.bgOverlay }}>
      <div className="rounded-2xl px-8 py-6 border" style={{ backgroundColor: modalBg, borderColor: t.border }}>
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: t.border, borderTopColor: t.primary }} />
          <span className="text-sm font-medium" style={{ color: t.textMuted }}>Chargement…</span>
        </div>
      </div>
    </div>, document.body
  );

  return ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: t.bgOverlay, animation: 'lmFadeIn 0.2s ease-out' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>

      <div className="rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col border"
        style={{ backgroundColor: modalBg, borderColor: t.border, boxShadow: t.shadowLg, animation: 'lmSlideUp 0.25s ease-out' }}>

        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0 border-b" style={{ borderColor: t.border }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.primaryDark})` }}>
              <Shield size={17} className="text-white" />
            </div>
            <div>
              <h2 className="text-[15px] font-bold" style={{ color: t.text }}>Gestion de la licence</h2>
              <p className="text-[11px] font-medium" style={{ color: t.textMuted }}>{company.company_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg transition-colors" style={{ color: t.textMuted }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = t.primarySofter}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
            <X size={18} />
          </button>
        </div>

        {/* Licence actuelle + Tabs */}
        <div className="px-6 pt-5 pb-0 flex-shrink-0" style={{ backgroundColor: bodyBg }}>
          {licence && <LicenceSummary licence={licence} t={t} accent={currentAccent} />}
          <div className="flex gap-0.5 mt-4 border-b" style={{ borderColor: t.border }}>
            <TabBtn active={activeTab === 'upgrade'} icon={TrendingUp} label="Changer de plan" onClick={() => setActiveTab('upgrade')} t={t} />
            <TabBtn active={activeTab === 'custom'}  icon={Sliders}    label="Personnaliser"   onClick={() => setActiveTab('custom')}  t={t} />
            <TabBtn active={activeTab === 'version'} icon={Zap} label="Version" onClick={() => setActiveTab('version')} t={t} /> 
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-6" style={{ backgroundColor: bodyBg }}>

          {/* TAB UPGRADE */}
          {activeTab === 'upgrade' && (
            <div style={{ animation: 'lmTabIn 0.2s ease-out' }}>
              {Object.keys(plans).length === 0 ? (
                <div className="flex items-center justify-center py-14">
                  <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: t.border, borderTopColor: t.primary }} />
                  <span className="ml-3 text-sm" style={{ color: t.textMuted }}>Chargement…</span>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(plans).map(([key, plan]) => {
                    const accent    = PLAN_ACCENTS[key] || t.primary;
                    const selected  = selectedType === key;
                    const isCurrent = licence?.type_licence === key;

                    return (
                      <div key={key} onClick={() => setSelectedType(key)}
                        className="relative flex flex-col p-5 rounded-xl border-2 cursor-pointer transition-all duration-200"
                        style={{ backgroundColor: selected ? accent + '08' : t.bgCard, borderColor: selected ? accent : t.border, boxShadow: selected ? t.shadow : 'none' }}
                        onMouseEnter={e => { if (!selected) e.currentTarget.style.borderColor = accent + '50'; }}
                        onMouseLeave={e => { if (!selected) e.currentTarget.style.borderColor = t.border; }}>

                        {isCurrent && (
                          <span className="absolute -top-2 left-4 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                            style={{ backgroundColor: accent, color: '#fff' }}>Actuel</span>
                        )}

                        {/* Titre + radio */}
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-[14px] font-bold capitalize" style={{ color: t.text }}>{plan.label || key}</span>
                          <div className="rounded-full border-2 flex items-center justify-center"
                            style={{ borderColor: selected ? accent : t.textMuted + '40', backgroundColor: selected ? accent : 'transparent', width: 18, height: 18 }}>
                            {selected && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                          </div>
                        </div>

                        {/* Limites — sans stockage */}
                        <div className="space-y-2.5 mb-3">
                          <PlanStat icon={Database} label={`${plan.max_employes} employés`}        accent={accent} t={t} />
                          <PlanStat icon={Users}    label={`${plan.max_utilisateurs} utilisateurs`} accent={accent} t={t} />
                        </div>

                        {/* Prix + frais */}
                        {(plan.prix > 0 || plan.frais_mise_en_place > 0) && (
                          <div className="pt-3 border-t mb-3" style={{ borderColor: t.borderLight }}>
                            {plan.prix > 0 && (
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-[10px] font-semibold" style={{ color: t.textMuted }}>Mensuel</span>
                                <span className="text-[12px] font-bold" style={{ color: accent }}>
                                  {Number(plan.prix).toLocaleString('fr-FR')} FCFA
                                </span>
                              </div>
                            )}
                            {plan.frais_mise_en_place > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-[10px] font-semibold" style={{ color: t.textMuted }}>Mise en place</span>
                                <span className="text-[11px] font-semibold" style={{ color: t.textSecondary }}>
                                  {Number(plan.frais_mise_en_place).toLocaleString('fr-FR')} FCFA
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Modules */}
                        {plan.modules && plan.modules.length > 0 && (
                          <div className="pt-3 border-t" style={{ borderColor: t.borderLight }}>
                            <div className="text-[10px] font-bold uppercase tracking-[0.12em] mb-2" style={{ color: t.textMuted }}>Modules</div>
                            <div className="flex flex-wrap gap-1">
                              {plan.modules.map((mod, i) => (
                                <span key={i} className="px-2 py-0.5 rounded text-[10px] font-semibold capitalize"
                                  style={{ backgroundColor: accent + '15', color: accent }}>{mod}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB CUSTOM — 2 champs (stockage supprimé) */}
          {activeTab === 'custom' && (
            <div className="rounded-xl border p-5" style={{ backgroundColor: t.bgCard, borderColor: t.border, animation: 'lmTabIn 0.2s ease-out' }}>
              <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg border mb-5"
                style={{ backgroundColor: t.primarySofter, borderColor: t.primaryBorder }}>
                <Info size={14} style={{ color: t.primaryText }} className="flex-shrink-0 mt-0.5" />
                <p className="text-[12px] font-medium" style={{ color: t.primaryText }}>
                  Les limites personnalisées outrepassent les valeurs du plan standard.
                </p>
              </div>
              {/* Limites de base */}
              <p className="text-[11px] font-bold uppercase tracking-[0.1em] mb-3" style={{ color: t.textMuted }}>
                Limites incluses dans le plan
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <Label t={t}>Max. employés</Label>
                  <NumberInput value={customLimits.max_employes} min={1} t={t}
                    onChange={e => setCustomLimits(p => ({ ...p, max_employes: parseInt(e.target.value) || 0 }))} />
                </div>
                <div>
                  <Label t={t}>Max. utilisateurs</Label>
                  <NumberInput value={customLimits.max_utilisateurs} min={1} t={t}
                    onChange={e => setCustomLimits(p => ({ ...p, max_utilisateurs: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>

              {/* Add-ons facturables */}
              <div className="p-4 rounded-xl border mb-1" style={{ background: 'rgba(245,158,11,0.05)', borderColor: 'rgba(245,158,11,0.25)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    ✦ Add-ons facturables
                  </span>
                  <span style={{ fontSize: 10, color: t.textMuted }}>— s'ajoutent au montant mensuel</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label t={t}>Utilisateurs sup. (× 3 500 FCFA/mois)</Label>
                    <NumberInput value={customLimits.addon_utilisateurs} min={0} step={1} t={t}
                      onChange={e => setCustomLimits(p => ({ ...p, addon_utilisateurs: parseInt(e.target.value) || 0 }))} />
                    {customLimits.addon_utilisateurs > 0 && (
                      <p style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, marginTop: 4, textAlign: 'right' }}>
                        + {(customLimits.addon_utilisateurs * 3500).toLocaleString('fr-FR')} FCFA/mois
                      </p>
                    )}
                  </div>
                  <div>
                    <Label t={t}>Employés sup. (tranches 10 × 2 500 FCFA)</Label>
                    <NumberInput value={customLimits.addon_employes} min={0} step={10} t={t}
                      onChange={e => setCustomLimits(p => ({ ...p, addon_employes: parseInt(e.target.value) || 0 }))} />
                    {customLimits.addon_employes > 0 && (
                      <p style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, marginTop: 4, textAlign: 'right' }}>
                        + {(Math.ceil(customLimits.addon_employes / 10) * 2500).toLocaleString('fr-FR')} FCFA/mois
                      </p>
                    )}
                  </div>
                </div>
                {(customLimits.addon_utilisateurs > 0 || customLimits.addon_employes > 0) && (
                  <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>Total add-ons mensuel</span>
                    <span style={{ fontSize: 14, fontWeight: 900, color: '#f59e0b' }}>
                      + {((customLimits.addon_utilisateurs * 3500) + (Math.ceil(customLimits.addon_employes / 10) * 2500)).toLocaleString('fr-FR')} FCFA
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}


          {activeTab === 'version' && (
          <div className="rounded-xl border p-5" style={{ backgroundColor: t.bgCard, borderColor: t.border, animation: 'lmTabIn 0.2s ease-out' }}>
            
            {/* Info */}
            <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg border mb-5"
              style={{ backgroundColor: t.primarySofter, borderColor: t.primaryBorder }}>
              <Zap size={14} style={{ color: t.primaryText }} className="flex-shrink-0 mt-0.5" />
              <p className="text-[12px] font-medium" style={{ color: t.primaryText }}>
                La version est affichée dans la section Licence de chaque entreprise cliente.
                Convention : <strong>MAJEUR.MINEUR.PATCH</strong> — ex: 1.0.1 pour un fix, 1.1.0 pour une fonctionnalité.
              </p>
            </div>

            {/* Version */}
            <div className="mb-5">
              <Label t={t}>Numéro de version</Label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={versionData.version_app}
                  onChange={e => setVersionData(p => ({ ...p, version_app: e.target.value }))}
                  placeholder="ex: 1.0.1"
                  className="w-full px-3.5 py-2.5 rounded-lg text-sm font-mono font-bold border outline-none transition-all"
                  style={{ backgroundColor: t.bgInput, borderColor: t.border, color: t.text, paddingLeft: 40 }}
                  onFocus={e => { e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
                  onBlur={e  => { e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
                />
                <Tag size={14} style={{ color: t.textMuted, position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              </div>
            </div>

            {/* Notes changelog */}
            <div>
              <Label t={t}>Notes de version (changelog)</Label>
              <textarea
                value={versionData.notes}
                onChange={e => setVersionData(p => ({ ...p, notes: e.target.value }))}
                placeholder="Ex: Ajout ProspectionHub, reset mot de passe, animations globales..."
                rows={5}
                className="w-full px-3.5 py-2.5 rounded-lg text-sm border outline-none transition-all resize-none"
                style={{ backgroundColor: t.bgInput, borderColor: t.border, color: t.text, fontFamily: 'DM Sans, sans-serif' }}
                onFocus={e => { e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
                onBlur={e  => { e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
              />
              <p className="text-[11px] mt-1.5 font-medium" style={{ color: t.textMuted }}>
                Ces notes sont visibles uniquement depuis le centre d'administration.
              </p>
            </div>

          </div>
        )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex items-center justify-end gap-3 flex-shrink-0 border-t"
          style={{ borderColor: t.border, backgroundColor: modalBg }}>
          <button onClick={onClose} disabled={saving}
            className="px-5 py-2.5 rounded-lg text-sm font-medium border transition-all duration-200"
            style={{ borderColor: t.border, color: t.textSecondary }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = t.primarySofter; e.currentTarget.style.borderColor = t.primaryBorder; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = t.border; }}>
            Annuler
          </button>
          <button onClick={
              activeTab === 'upgrade' ? () => handleUpgrade(false) :
              activeTab === 'version' ? handleSaveVersion :
              handleSaveCustom
            }
            disabled={saving || (activeTab === 'upgrade' && (!selectedType || Object.keys(plans).length === 0))}
            className="px-6 py-2.5 rounded-lg text-sm font-bold text-white transition-all duration-200 flex items-center gap-2 disabled:opacity-40 active:scale-[0.97]"
            style={{ backgroundColor: t.primary }}
            onMouseEnter={e => { if (!saving) e.currentTarget.style.backgroundColor = t.primaryDark; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = t.primary; }}>
            {saving
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {activeTab === 'upgrade' ? 'Changement…' : 'Enregistrement…'}
                </>
              : <><CheckCircle size={15} />
                  {activeTab === 'upgrade' ? 'Appliquer le plan' :
                  activeTab === 'version' ? 'Enregistrer la version' :
                  'Enregistrer'}
                </>}
          </button>
        </div>
      </div>

      {toast.show && (
        <Toast show={toast.show} message={toast.message} type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })} />
      )}

      {confirmOverride && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ maxWidth: 420, width: '100%', borderRadius: 16, background: t.bgCard, border: `1px solid ${t.border}`, padding: 24 }}>
            <p style={{ fontSize: 14, fontWeight: 800, color: t.text, margin: '0 0 8px' }}>⚠️ Overrides détectés</p>
            <p style={{ fontSize: 12, color: t.textSecondary, margin: '0 0 16px', lineHeight: 1.6 }}>{confirmOverride.message}</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {confirmOverride.overrides.map(o => (
                <span key={o.flag_key} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, background: t.bgInput, color: t.textMuted, border: `1px solid ${t.border}` }}>{o.flag_key}</span>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setConfirmOverride(null)}
                style={{ padding: '8px 16px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={handleConfirmForce}
                style={{ padding: '8px 16px', borderRadius: 8, background: '#ef4444', border: 'none', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                Forcer l'application
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes lmFadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes lmSlideUp { from{opacity:0;transform:translateY(12px) scale(0.98)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes lmTabIn   { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>,
    document.body
  );
};

export default LicenceManagementModal;